import axios from 'axios';
import {
  QuestionsResponseSchema,
  type ExamQuestion,
} from '../../domain/entities/question.entity';

/**
 * A busca das questões do ensaio na API REST da Aristo (repo `student`,
 * `rest-api/enamed-feedback`), **pelos ids do mapper** (`ensaioQuestionIds`).
 *
 * Os ids vão no **corpo** de um `POST`: são dezenas de ObjectIds, e uma query
 * string desse tamanho esbarra em limite de proxy e polui log de acesso. A
 * resposta é `{ totalQuestions, returnedQuestions, questions }`, com o mesmo
 * objeto de questão da prova oficial (`/exam/:templateId`).
 *
 * **A rota é pública com whitelist**: só responde (senão 403) se os ids forem
 * **exatamente** os de um ensaio liberado no `ALLOWED_PRACTICE_EXAMS` do
 * `student` — sem sobrar, faltar nem repetir. Mudou o `ensaioQuestionIds` de
 * uma edição aqui, muda a whitelist lá no mesmo deploy.
 */
export const QUESTIONS_ROUTE = '/v1/enamed-feedback/practice-exam';

/**
 * **Não passa pelo `apiClient`**: é outro host. O `x-scope-id` do Primum Core
 * só faria o navegador pedir uma checagem de CORS que a rota não precisa
 * aceitar.
 *
 * Rejeita em vez de devolver um status: quem consome é o TanStack Query, que
 * transforma rejeição em estado de erro.
 */
export class ExamApiDatasource {
  private readonly baseUrl: string;

  constructor(baseUrl: string = import.meta.env.VITE_ARISTO_API_URL ?? '') {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  async getQuestions(questionIds: readonly string[]): Promise<ExamQuestion[]> {
    // Sem host não há para onde ir: o `axios` montaria uma URL relativa e
    // pediria as questões à própria LP, que devolveria o `index.html`.
    if (!this.baseUrl) throw new Error('VITE_ARISTO_API_URL não configurada');

    const { data } = await axios.post<unknown>(
      `${this.baseUrl}${QUESTIONS_ROUTE}`,
      { questionIds },
    );
    return QuestionsResponseSchema.parse(data).questions;
  }
}
